import Vue from 'vue';

export default {
  namespaced: true,
  state: {
    filing: null,
    currentFiling: null,
    currentFilingId: null,
    certificationType: null,
  },
  getters: {
    getCurrentFilingScreenEndpoint: (state) => (screen) => {
      if (!state.currentFilingId || !screen) {
        throw('Could not retrieve the screen endpoint');
      }
      return `/filings/${state.currentFilingId}/${screen}`;
    },
  },
  mutations: {
    setCertificationType(state, type) {
      state.certificationType = type;
    },
    setCurrentFilingId (state, filingId) {
      Vue.set(state, 'currentFilingId', filingId);
    },
    setCurrentFiling(state, filing) {
      Vue.set(state, 'currentFiling', filing);
    },
    setCurrentFilingPositions(state, filingPositions) {
      Vue.set(state, 'currentFilingPositions', filingPositions);
    },
    setCurrentFilingFilerStatus(state, filerStatus) {
      Vue.set(state, 'currentFilerStatus', filerStatus);
    },
    setFiling(state, filing) {
      Vue.set(state, 'filing', filing);
    },
    clearCurrentFiling(state) {
      Vue.set(state, 'filing', null);
      Vue.set(state, 'currentFilingId', null);
    },
  },
  actions: {
    checkCurrentFilingId ({ commit, state }) {
      try {
        let cachedCurrentFilingId = state.currentFilingId || this.$sCache.get('currentFilingId');
        if (!cachedCurrentFilingId) {
          throw ('No current filing id available');
        } else {
          commit('setCurrentFilingId', cachedCurrentFilingId);
          return true;
        }
      } catch (e) {
        return false;
      }
    },
    async createNewFiling ({ commit, rootState }, filing) {

      filing.userID = rootState.login.loggedIn.id;
      let year = filing.year.toString();
      filing.year = year;

      try {
        let newFiling = await this.$axios({
          method: 'post',
          url: `${rootState.endpoints.filings}`,
          data: filing,
        });
        if (newFiling.status === 201) {
          commit('setCurrentFilingId', newFiling.data.id);
          this.$sCache.set('currentFilingId', newFiling.data.id);
          return true;
        }
        this.$error('There was an issue creating a new filing. Please try refreshing the page first. If the issue persists please contact support.');
        return false;

      } catch (e) {
        this.$error('There was an issue creating a new filing. Please try refreshing the page first. If the issue persists please contact support.');
        return false;
      }
    },
    deleteFilingDraft ({ rootState }, filingId) {
      return new Promise((resolve, reject) => {
        this.$axios({
          method: 'delete',
          url: `${rootState.endpoints.filings}/${filingId}`,
        }).then(response => {
          resolve(response);
        }).catch(error => {
          reject(error);
        });
      });
    },
    async fetchFiling({ commit, rootState }) {
      try {
        let currentFilingId = rootState.filing.currentFilingId;

        if (!currentFilingId) {
          throw ('currentFilingId is not set');
        }

        try {
          let filing = await this.$axios.get(`${rootState.endpoints.filings}/${currentFilingId}`);
          if (filing.status === 200) {
            commit('filing/setFiling', filing.data, { root: true });
          }
        } catch (e) {
          console.log(e);
        }

      } catch (e) {
        console.log(e);
      }
    },
    fetchCurrentFilingFilerStatus ({ commit, rootState }) {
      return this.$axios.get(`${rootState.endpoints.filings}/${rootState.filing.currentFilingId}/filer-statuses`).then(response => {
        commit('setCurrentFilingFilerStatus', response.data);
      }).catch(error => {
        console.warn(error);
      });
    },
    fetchCurrentFilingPositions ({ commit, rootState }) {
      return this.$axios.get(`${rootState.endpoints.filings}/${rootState.filing.currentFilingId}/positions`).then(response => {
        commit('setCurrentFilingPositions', response.data);
      }).catch(error => {
        console.warn(error);
      });
    },
    saveFilingFilerStatuses ({ rootState }, filerStatuses) {
      return new Promise(async (resolve, reject) => {
        let promises = [];

        for (let type in filerStatuses) {
          if (filerStatuses[type].length > 0) {
            promises.push(this.$axios({
              method: type,
              url: `${rootState.endpoints.filings}/${rootState.filing.currentFilingId}/filer-statuses`,
              data: filerStatuses[type],
            }).catch(error => {
              console.warn(error);
            }));
          }
        }

        Promise.all(promises).then(response => {
          resolve(response);
          return;
        }).catch(error => {
          reject(console.warn(error));
        });
      });
    },
    saveFiling({ rootState }, filing) {
      return new Promise((resolve, reject) => {

        //can't send this on a patch
        delete filing.statePdfUrl;
        delete filing.cityPdfUrl;
        delete filing.mayorPdfUrl;

        this.$axios({
          method: 'patch',
          url: `${rootState.endpoints.filings}/${filing.id}`,
          data: filing,
        }).then(response => {
          resolve(response);
          return;
        }).catch(error => {
          reject(console.warn(error));
        });
      });
    },
    saveFilingScreen({ getters }, screen) {
      return new Promise(async (resolve, reject) => {
        let currentScreen = { ...screen };

        let cud = {
          post: [],
          put: [],
          delete: [],
        };

        currentScreen.items.forEach(item => {
          if (item.cud) {
            if (item.cud === 'post') {
              item.id = undefined;
              cud[item.cud].push(item);
            } else if (item.cud === 'put') {
              cud[item.cud].push(item);
            } else if (item.cud === 'delete') {
              cud[item.cud].push(item.id);
            }
          }
        });

        let promises = [];
        let endpoint = '';
        let ready = true;

        try {
          endpoint = getters['getCurrentFilingScreenEndpoint'](currentScreen.endpoint);
        } catch (error) {
          ready = false;
          console.warn(error);
        }

        if (ready) {
          for (let type in cud) {
            if (cud[type].length > 0 && type !== 'delete') {
              promises.push(this.$axios({
                method: type,
                url: endpoint,
                data: cud[type],
              }).catch(error => {
                console.warn(error);
              }));
            }
          }

          //Split saving request to keep "delete" from running before the other requests.
          //This stops the backend rule (user needs to have at least one position) from triggering
          await Promise.all(promises);
          if (cud['delete'].length > 0) {
            await this.$axios({
              method: 'delete',
              url: endpoint,
              data: cud['delete'],
            }).catch(error => {
              reject(error);
            });
          }

          resolve('all done');
        }
      });
    },
    uploadPaperFiling({ rootState }, formData) {

      if (!rootState.user.tempId) {
        this.$warning('Unable to proceed with upload. Please contact support.');
      }

      return this.$axios({
        method: 'post',
        url: `admin/users/${rootState.user.tempId}/paper-filings`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then(response => {
        console.log(response);
      }).catch(error => console.warn(error));
    },
    uploadHistoricalFiling({ rootState }, formData) {

      if (!rootState.user.tempId) {
        this.$warning('Unable to proceed with upload. Please contact support.');
      }

      return this.$axios({
        method: 'post',
        url: `admin/users/${rootState.user.tempId}/historical-upload`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then(response => {
        console.log(response);
      }).catch(error => console.warn(error));
    },
    uploadPaperRedaction({ rootState }, formData) {

      if (!rootState.user.tempId) {
        this.$warning('Unable to proceed with upload. Please contact support.');
      }

      return this.$axios({
        method: 'post',
        url: `admin/users/${rootState.user.tempId}/paper-redactions`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then(response => {
        console.log(response);
      }).catch(error => console.warn(error));
    },
    // eslint-disable-next-line no-unused-vars
    onlineRedaction ({ rootState }, pdfData) {
      try {
        let request = this.$axios({
          method: 'post',
          url: `/admin/filings/electronic-redactions`,
          data: pdfData,
        });
        return request;
      } catch (e) {
        this.$warning(e.message);
        return false;
      }
    },
    // eslint-disable-next-line no-unused-vars
    publishFiling ({ rootState }, pdfData) {
      try {
        let request = this.$axios({
          method: 'post',
          url: `/admin/filings/publish-filings `,
          data: pdfData,
        });
        return request;
      } catch (e) {
        this.$warning(e.message);
        return false;
      }
    },
    async deleteRedaction ({ rootState }, type) {
      try {

        let request = await this.$axios({
          method: 'delete',
          url: `/admin/filings/${rootState.filing.currentFilingId}/${type}`,
        });
        return request;
      } catch (e) {
        console.log(JSON.stringify(e));
        this.$warning(e.message);
        return false;
      }
    },
    // eslint-disable-next-line no-unused-vars
    getAdminPdf ( { commit }, pdfData) {
      return new Promise((resolve, reject) => {
        this.$axios({
          method: 'post',
          url: `/admin/pdf-service`,
          data: pdfData,
        }).then(response => {
          resolve(response.data.pdfSignedUrl);
        }).catch(error => {
          console.warn(error);
          reject();
        });
      });
    },
    // eslint-disable-next-line no-unused-vars
    getPdf ({ commit }, pdfData) {
      return new Promise((resolve, reject) => {
        this.$axios({
          method: 'post',
          url: `/pdf-service`,
          data: pdfData,
        }).then(response => {
          resolve(response.data.pdfSignedUrl);
        }).catch(error => {
          console.warn(error);
          reject();
        });
      });
    },
  },
};
